<template>
  <div :class="b('', modifiers)">
    <component
      :is="isOverflowing === true ? 'c-tooltip' : 'div'"
      :popper-options="{ placement: 'top', strategy: 'fixed' }"
    >
      <span v-if="prefix">{{ (prefix || '').trim() }}&nbsp;</span>
      <component
        v-for="(entry, entryIndex) in compiledEntries"
        :is="entry.url && !isOverflowing ? 'a' : 'span'"
        :key="entryIndex"
        :href="entry.url"
      >
        <span>{{ entry.text }}</span>

        <e-icon-sprite
          v-if="entry.url && !isOverflowing"
          icon="i-chevron-2--right"
        />
      </component>

      <template #tooltip>
        <div
          :class="b('tooltip')"
          :style="tooltipStyle"
        >
          <component
            v-for="(entry, entryIndex) in compiledEntries"
            :is="entry.url ? 'a' : 'span'"
            :key="entryIndex"
            :href="entry.url"
          >
            <span>{{ entry.text }}</span>
          </component>
        </div>
      </template>
    </component>
  </div>
</template>

<script>
  import eIconSprite from '@/elements/e-icon-sprite.vue';

  /**
   * Renders a fixed height list of inline texts. If it overflows the whole list will be shown in a tooltip.
   */
  export default {
    name: 'c-overflow-text-list',
    components: {
      eIconSprite,
    },

    // mixins: [],

    props: {
      /**
       * A list of elements to.
       *
       * e.g. `[{ text: 'Text without url' }, { text: 'With link', url: 'https://ivoclar.com' }, ...]
       */
      entries: {
        type: Array,
        required: true,
      },

      /**
       * Separator text to join entries by.
       */
      separator: {
        type: String,
        default: ', ',
      },

      /**
       * Prefix text to prepend.
       */
      prefix: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        /**
         * Keeps track of the current overflow state.
         */
        isOverflowing: null,

        /**
         * Cloned style properties for the tooltip.
         */
        tooltipStyle: {},
      };
    },

    computed: {
      /**
       * Computes modifiers classes.
       *
       * @returns {Object}
       */
      modifiers() {
        return {
          hidden: this.isOverflowing === null,
          overflowing: this.isOverflowing === true,
        };
      },

      /**
       * Compiles the list entries their renderable form.
       *
       * @returns {Array}
       */
      compiledEntries() {
        return this.entries.flatMap((entry, index) => {
          const result = [
            {
              text: entry.text.trim(),
              url: entry.url,
            },
          ];

          if (index < this.entries.length - 1) {
            result.push({
              text: this.separator,
            });
          }

          return result;
        });
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.$nextTick(this.updateStyling);

      window.addEventListener('resize', this.updateStyling);
    },
    // beforeUpdate() {},
    // updated() { },
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('resize', this.updateStyling);
    },
    // unmounted() {},

    methods: {
      /**
       * Checks if children are overflowing and clones the styling for the tooltip.
       */
      updateStyling() {
        const element = this.$el;
        const state = element ? element.scrollHeight - element.clientHeight > 3 /* threshold */ : null;

        if (state === this.isOverflowing) {
          return;
        }
        this.isOverflowing = state;

        const source = (element && typeof getComputedStyle === 'function' && getComputedStyle(element)) || {};
        const keys = ['font-size', 'font-weight', 'font-family', 'line-height'];
        const clone = {};

        for (const key of keys) {
          clone[key] = source[key];
        }

        this.tooltipStyle = clone;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  // @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  // @use '@/setup/scss/functions';
  // @use '@/setup/scss/extends' as *;

  .c-overflow-text-list {
    --line-clamp: 2;

    $this: &;

    position: relative;
    height: 5em;
    overflow: hidden;
    background: variables.$color-grayscale--1000;

    &--hidden {
      * {
        opacity: 0 !important; // stylelint-disable-line declaration-no-important
      }
    }

    &--overflowing {
      @include mixins.line-clamp('var(--line-clamp, 2)', 1.5);
    }

    &__tooltip {
      pointer-events: all;
    }

    a {
      color: inherit;

      &:hover,
      &:focus {
        color: inherit;
      }

      .e-icon-sprite {
        @include mixins.showMoreIcon();

        display: inline-block;
        vertical-align: middle;
      }
    }
  }
</style>
